import api from "@/lib/api";

// Convert URL-safe base64 (VAPID pubkey) to Uint8Array (required by PushManager).
function urlBase64ToUint8Array(value) {
  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

export function isPushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function currentPermission() {
  return typeof Notification === "undefined" ? "unsupported" : Notification.permission;
}

export async function ensureServiceWorker() {
  if (!("serviceWorker" in navigator)) return null;
  // Register only once — browsers dedupe by scope but this avoids repeat network calls.
  try {
    const existing = await navigator.serviceWorker.getRegistration("/");
    if (existing) return existing;
    return await navigator.serviceWorker.register("/sw.js", { scope: "/" });
  } catch (e) {
    console.warn("SW register failed:", e?.message || e);
    return null;
  }
}

/**
 * Prompt the user for notification permission and subscribe to Web Push.
 * MUST be called from a user-gesture handler (button click).
 * Returns { ok, reason } — reason ∈ 'unsupported' | 'denied' | 'error' | undefined.
 */
export async function enablePush() {
  if (!isPushSupported()) return { ok: false, reason: "unsupported" };

  const permission = await Notification.requestPermission();
  if (permission !== "granted") return { ok: false, reason: "denied" };

  const reg = await ensureServiceWorker();
  if (!reg) return { ok: false, reason: "error" };

  try {
    // Wait for the SW to be truly active before subscribing.
    await navigator.serviceWorker.ready;

    const { data } = await api.get("/push/vapid-public-key");
    const publicKey = data?.publicKey;
    if (!publicKey) return { ok: false, reason: "error" };

    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
    }

    await api.post("/push/subscribe", sub.toJSON());
    return { ok: true };
  } catch (e) {
    console.warn("enablePush failed:", e?.message || e);
    return { ok: false, reason: "error" };
  }
}

export async function disablePush() {
  if (!isPushSupported()) return { ok: false };
  try {
    const reg = await navigator.serviceWorker.getRegistration("/");
    const sub = await reg?.pushManager.getSubscription();
    if (sub) {
      const json = sub.toJSON();
      await api.post("/push/unsubscribe", json).catch(() => {});
      await sub.unsubscribe();
    }
    return { ok: true };
  } catch (e) {
    console.warn("disablePush failed:", e?.message || e);
    return { ok: false };
  }
}
